import { createSelector } from '@reduxjs/toolkit';
import { selectors as dataGridTableSelectors } from 'containers/Pages/Region/Companies/Modules/dataGridTable';
import { isEmpty } from 'lodash';
import { selectors as filterSelectors } from 'containers/Pages/Region/Companies/Modules/filters';
import { getItemOrDefaultToValue, getPersonName } from 'utils/details';
import { getCountryList } from 'reducers/utils';

export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);
export const getTotalEntries = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => {
    if (!rowsCount) {
      return 0;
    }
    return rowsCount;
  },
);
export const getFilters = createSelector(
  filterSelectors.getFilterValues,
  filterValues => {
    return filterValues;
  },
);
export const getPrefixForCountry = (country, countries) => {
  const prefix = '';
  if (isEmpty(countries) || isEmpty(country)) return prefix;
  const countryInfo = countries.find(c => c.code === country);
  if (isEmpty(countryInfo)) return prefix;
  return countryInfo.dialCode;
};
export const getCompanyDetails = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  data => {
    if (isEmpty(data)) return {};
    return {
      generalInfo: {
        region: {
          label: 'Region',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'regionName', '-'),
        },
        companyName: {
          label: 'Name',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'name', '-'),
        },
        archived: {
          type: 'boolean',
          value: data.archived,
        },
        accountHolder: {
          label: 'Account Holder',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'accountHolder', '-'),
        },
        email: {
          label: 'Email',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'email', '-'),
        },
        phone: {
          label: 'Phone',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'phone', '-'),
        },
        services: {
          type: 'icons',
          value: getItemOrDefaultToValue(data, 'services', []),
        },
      },
      detailedInfo: {
        numSites: {
          label: 'Sites',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numSites', '0'),
        },
        numManagers: {
          label: 'Managers',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numManagers', '0'),
        },
        numSupervisors: {
          label: 'Supervisors',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numSupervisors', '0'),
        },
        numResponders: {
          label: 'Responders',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numResponders', '0'),
        },
        numGuards: {
          label: 'Guards',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numGuards', '0'),
        },
        numCleaners: {
          label: 'Cleaners',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numCleaners', '0'),
        },
        numMaintainers: {
          label: 'Maintainers',
          type: 'text',
          value: getItemOrDefaultToValue(data, 'numMaintainers', '0'),
        },
      },
    };
  },
);
export const getCompanyList = createSelector(
  dataGridTableSelectors.getGridTableList,
  getCountryList,
  (list, countries) => {
    if (isEmpty(list)) return [];
    return list.map(company => ({
      archived: company.archived,
      regionName: company.regionName,
      id: company.id,
      name: company.name,
      services: company.services,
      accountHolder: getPersonName(company.contact.general),
      email: company.contact.general.email,
      phone:
        company.contact.general.phoneNumber !== null
          ? `${getPrefixForCountry(
              getItemOrDefaultToValue(
                company.contact.general.phoneNumber,
                'country',
              ),
              countries,
            )} ${getItemOrDefaultToValue(
              company.contact.general.phoneNumber,
              'number',
              '-',
            )}`
          : '-',
      numSites: company.resourceCount.sites,
      numManagers: company.resourceCount.managers,
      numSupervisors: company.resourceCount.supervisors,
      numResponders: company.resourceCount.responders,
      numGuards: company.resourceCount.guards,
      numCleaners: company.resourceCount.cleaners,
      numMaintainers: company.resourceCount.maintainers,
    }));
  },
);
